import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  ArrowUpRight,
  Briefcase,
  Landmark,
  Rocket,
  TrendingUp,
  Zap,
  Building2,
  GraduationCap,
  Bot,
  Library,
  ExternalLink,
} from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const specializations = [
  { name: "Investment Banking", icon: Briefcase, slug: "investment-banking", status: "live" },
  { name: "Private Equity", icon: Landmark, slug: "private-equity", status: "live" },
  { name: "Venture Capital", icon: Rocket, slug: "venture-capital", status: "live" },
  { name: "Energy Finance", icon: Zap, slug: "energy", status: "live" },
  { name: "Hedge Funds", icon: TrendingUp, slug: "hedge-funds", status: "live" },
  { name: "Real Estate Finance", icon: Building2, slug: "real-estate", status: "live" },
];

const highlights = [
  {
    title: "Career Hubs",
    icon: GraduationCap,
    body: "Explore structured career guides featuring career roadmaps, essential skills, certifications, ebooks, recommended learning materials, affiliate recommendations, and premium playbooks.",
    href: "/roadmaps",
    label: "Explore Career Hubs",
  },
  {
    title: "Finance Career AI",
    icon: Bot,
    body: "Ask career, recruiting, resume, technical interview, valuation, financial modeling, networking, and finance learning questions. VantStride AI is designed specifically for finance careers.",
    href: "/career-ai",
    label: "Try the AI",
  },
  {
    title: "Ebooks & Playbooks",
    icon: Library,
    body: "Go deeper with focused ebooks and comprehensive career playbooks built to turn complex finance and professional topics into clear, practical learning.",
    links: [
      { label: "Explore Ebooks", href: "/ebooks" },
      { label: "Explore Playbooks", href: "/playbooks" },
    ],
  },
];

const partners = [
  {
    name: "Wall Street Oasis",
    tagline:
      "A finance career platform combining industry courses, financial modelling, interview preparation, and a large professional community.",
    url: "https://www.wallstreetoasis.com/courses?affiliate=ac_1017137&utm_source=affiliate&utm_medium=ac_1017137&utm_campaign=affiliate",
    tag: "Community & Learning",
  },
  {
    name: "TradingView",
    tagline:
      "A practical market platform for charts, screeners, financial data, and developing stronger market awareness.",
    url: "https://www.tradingview.com",
    tag: "Markets & Charts",
  },
  {
    name: "Udemy",
    tagline:
      "A broad learning marketplace for building practical skills across finance, modelling, valuation, markets, and beyond.",
    url: "https://trk.udemy.com/KBG9mz",
    tag: "Courses & Learning",
  },
];

export default function Landing() {
  return (
    <div className="vestryn-soft-gradient">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 vestryn-dot-bg opacity-70" aria-hidden />
        <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-[#0A2540]/5 blur-3xl" aria-hidden />
        <div className="absolute top-40 -left-20 w-[280px] h-[280px] rounded-full bg-[#C6A15B]/[0.06] blur-3xl" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 pb-6 sm:pb-8">
          <div className="max-w-4xl">
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-xs font-medium text-slate-600"
              data-testid="hero-badge"
            >
              <img
                src="/vs-monogram-navy.png"
                alt=""
                aria-hidden="true"
                className="w-3.5 h-3.5 object-contain"
              />
              <span>WE GIVE YOU THE MAP FIRST.</span>
            </motion.div>

            <motion.h1
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display mt-5 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-medium text-[#0A2540]"
              data-testid="hero-heading"
            >
              Your Edge, <span className="text-slate-400 italic">Before Day One.</span>
            </motion.h1>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl"
              data-testid="hero-subheading"
            >
              VantStride is a finance career preparation platform that helps students and aspiring professionals understand the path before they step into the industry. Explore careers through focused Career Hubs, see what the work demands, build the right skills, prepare with Playbooks and practical resources, and use Career AI to turn uncertainty into a clearer plan.
            </motion.p>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-7 flex flex-wrap items-center gap-3"
            >
              <Link
                to="/roadmaps"
                data-testid="hero-cta-primary"
                className="group relative inline-flex items-center gap-2 bg-[#0A2540] text-white rounded-full pl-6 pr-5 py-4 text-sm font-semibold hover:bg-[#051A2E] transition-all duration-300 active:scale-95 shadow-[0_10px_30px_rgba(10,37,64,0.25)] hover:shadow-[0_16px_40px_rgba(198,161,91,0.28),_0_10px_30px_rgba(10,37,64,0.25)] hover:-translate-y-0.5"
              >
                Explore Career Hubs
                <span className="ml-1 grid place-items-center w-7 h-7 rounded-full bg-white/15 group-hover:bg-white/25 transition-colors">
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
              <Link
                to="/career-ai"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white text-[#0A2540] rounded-full px-5 py-3 text-sm font-medium border border-slate-200 hover:bg-slate-50 transition-colors active:scale-95"
              >
                Try VantStride AI
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Strip — compact editorial bridge into the tracks section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-12 pb-6 sm:pb-8">
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5 }}
          data-testid="hero-value-strip"
        >
          <div
            className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500 mb-6 sm:mb-8"
            data-testid="value-strip-eyebrow"
          >
            How VantStride prepares you
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-8 md:gap-x-10">
            {[
              { n: "01", title: "Understand the career", body: "Know what the role actually involves." },
              { n: "02", title: "Build the skills", body: "Learn what matters before recruiting." },
              { n: "03", title: "Enter prepared", body: "Turn knowledge into a real career edge." },
            ].map((v, i) => (
              <div
                key={v.n}
                data-testid={`value-strip-item-${v.n}`}
                className={`md:pl-6 ${i > 0 ? "md:border-l md:border-slate-100" : ""}`}
              >
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-400">
                  {v.n}
                </div>
                <h3 className="font-display mt-2 text-xl sm:text-2xl text-[#0A2540] leading-snug">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Free Guide promo */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 pb-6 sm:pb-8">
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5 }}
          className="rounded-3xl bg-[#0A2540] text-white p-8 sm:p-12"
          data-testid="free-guide-promo"
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold vestryn-accent">
            Free · VantStride Guide
          </div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mt-3">
            Finance Interview Foundations Guide
          </h2>
          <p className="mt-3 text-slate-200 max-w-2xl leading-relaxed">
            Build your finance interview foundation before you go deeper. This free 38-page guide covers technical fundamentals, career paths, interview preparation, 112 interview-style questions, and a 14-day preparation system.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <a
              href="https://payhip.com/b/Eyemc"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="free-guide-cta"
              className="inline-flex items-center gap-2 bg-white text-[#0A2540] rounded-full px-5 py-3 text-sm font-semibold hover:bg-slate-100 transition-colors active:scale-95"
            >
              Get the Free Guide
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <span className="text-xs text-slate-400">Free download · 2026 Edition</span>
          </div>
        </motion.div>
      </section>

      {/* Career Tracks We Cover */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 pb-10">
        <div className="flex items-baseline justify-between">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">
            Career tracks we cover
          </div>
          <Link
            to="/roadmaps"
            className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-[#0A2540]"
          >
            See all Career Hubs <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {specializations.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
            >
              {s.status === "coming_soon" ? (
                <div
                  data-testid={`spec-${s.slug}`}
                  aria-disabled
                  className="group flex items-center justify-between gap-2 rounded-2xl border border-slate-100 bg-white px-4 py-3.5 text-sm text-slate-700 cursor-default"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <s.icon className="w-4 h-4 text-[#0A2540]/70 flex-shrink-0" />
                    <span className="truncate">{s.name}</span>
                  </span>
                  <span className="text-[9px] uppercase tracking-[0.14em] font-semibold text-[#7a5a24] border border-[#C6A15B]/40 bg-[#C6A15B]/10 rounded-full px-1.5 py-0.5 flex-shrink-0">
                    Soon
                  </span>
                </div>
              ) : (
                <Link
                  to={`/roadmaps/${s.slug}`}
                  data-testid={`spec-${s.slug}`}
                  className="group flex items-center justify-between gap-2 rounded-2xl border border-slate-100 bg-white px-4 py-3.5 text-sm text-slate-700 hover:border-[#0A2540]/25 vestryn-card-lift"
                >
                  <span className="flex items-center gap-2">
                    <s.icon className="w-4 h-4 text-[#0A2540]" />
                    {s.name}
                  </span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#0A2540] transition-colors" />
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* What you get */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">What you get</div>
          <h2 className="font-display mt-3 text-3xl sm:text-5xl tracking-tight text-[#0A2540] font-medium">
            A complete finance career platform
          </h2>
          <p className="mt-3 text-slate-600 text-lg leading-relaxed">
            VantStride helps students prepare for finance careers through structured Career Hubs, free learning resources, premium playbooks, curated recommendations, and a finance-focused AI assistant.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {highlights.map((h, i) => (
            <motion.div
              key={h.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="rounded-3xl border border-slate-100 bg-white p-7 shadow-[0_8px_30px_rgba(0,0,0,0.04)] vestryn-card-lift flex flex-col"
              data-testid={`highlight-${h.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-[#0A2540] text-white grid place-items-center">
                  <h.icon className="w-5 h-5" />
                </div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">
                  0{i + 1}
                </div>
              </div>
              <h3 className="font-display mt-5 text-2xl text-[#0A2540] font-medium">{h.title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed flex-1">{h.body}</p>
              {h.links ? (
                <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
                  {h.links.map((l) => (
                    <Link
                      key={l.href}
                      to={l.href}
                      data-testid={`highlight-link-${l.href.replace(/\//g, "")}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0A2540] group"
                    >
                      {l.label}
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  to={h.href}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#0A2540] group"
                >
                  {h.label}
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Recommended Learning Partners */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">
              Recommended Learning Partners
            </div>
            <h2 className="font-display mt-2 text-3xl sm:text-4xl tracking-tight text-[#0A2540] font-medium">
              Carefully selected platforms we recommend.
            </h2>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {partners.map((p, i) => (
            <motion.a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`partner-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group rounded-3xl border border-slate-100 bg-white p-7 vestryn-card-lift flex flex-col"
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 px-2 py-1 rounded-full bg-slate-100">
                  {p.tag}
                </span>
                <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-[#C6A15B] transition-colors" />
              </div>
              <h3 className="font-display text-2xl text-[#0A2540] font-medium mt-4">{p.name}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{p.tagline}</p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#0A2540]">
                Visit <ArrowUpRight className="w-4 h-4" />
              </span>
            </motion.a>
          ))}
        </div>
        <p className="mt-4 text-[11px] text-slate-400">
          VantStride is not affiliated with these platforms. Some links may be affiliate links. See our{" "}
          <Link to="/affiliate-disclosure" className="underline hover:text-[#0A2540]">
            Affiliate Disclosure
          </Link>
          .
        </p>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-16">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#0A2540] text-white p-9 sm:p-14">
          <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-white/5 blur-3xl" aria-hidden />
          <div className="absolute bottom-0 right-0 w-[180px] h-[2px] bg-gradient-to-r from-transparent to-[#C6A15B]/60" aria-hidden />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl sm:text-5xl tracking-tight font-medium">
              Prepare smarter. Land better.
            </h2>
            <p className="mt-4 text-slate-200 text-lg leading-relaxed">
              A structured way to navigate finance careers — explore Career Hubs to understand the path, use curated resources to build the right skills, prepare deeply with Playbooks, and turn questions into next steps with finance-only AI.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/roadmaps"
                data-testid="footer-cta-primary"
                className="inline-flex items-center gap-2 bg-white text-[#0A2540] rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-100 transition-colors active:scale-95"
              >
                Explore Career Hubs
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/career-ai"
                data-testid="footer-cta-secondary"
                className="inline-flex items-center gap-2 bg-white/10 text-white rounded-full px-6 py-3.5 text-sm font-medium border border-white/20 hover:bg-white/15 transition-colors active:scale-95"
              >
                Try VantStride AI
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
